/**
 * @typedef {[number, number]} LngLat
 * Coordinate pair in [lng, lat] order.
 */

/**
 * @typedef {[LngLat, LngLat, LngLat, LngLat]} FloorPlanCorners
 * Four floor plan corners in [top-left, top-right, bottom-right, bottom-left] order.
 */

/**
 * Compute the centroid (average center) of four corners.
 *
 * @param {FloorPlanCorners} corners
 * @returns {LngLat}
 */
export function computeCentroid(corners) {
  const [sumLng, sumLat] = corners.reduce(
    ([lngAcc, latAcc], [lng, lat]) => [lngAcc + lng, latAcc + lat],
    [0, 0]
  )

  return [sumLng / corners.length, sumLat / corners.length]
}

/**
 * Translate all corners by longitude/latitude deltas.
 *
 * @param {FloorPlanCorners} corners
 * @param {number} dLng
 * @param {number} dLat
 * @returns {FloorPlanCorners}
 */
export function translateCorners(corners, dLng, dLat) {
  return /** @type {FloorPlanCorners} */ (
    corners.map(([lng, lat]) => [lng + dLng, lat + dLat])
  )
}

/**
 * Scale corners uniformly from the opposite corner (anchor).
 *
 * @param {FloorPlanCorners} corners
 * @param {number} dragIndex
 * @param {LngLat} newPosition
 * @returns {FloorPlanCorners}
 */
export function scaleFromAnchor(corners, dragIndex, newPosition) {
  const anchorIndex = (dragIndex + 2) % 4
  const anchor = corners[anchorIndex]
  const originalDragged = corners[dragIndex]

  const originalDx = originalDragged[0] - anchor[0]
  const originalDy = originalDragged[1] - anchor[1]
  const originalDistance = Math.hypot(originalDx, originalDy)

  if (originalDistance === 0) {
    return /** @type {FloorPlanCorners} */ (corners.map(([lng, lat]) => [lng, lat]))
  }

  const newDx = newPosition[0] - anchor[0]
  const newDy = newPosition[1] - anchor[1]
  // Math.hypot is intentionally unsigned: prevents image flip when dragging past anchor.
  // 0.01 clamp prevents collapse to degenerate near-zero size.
  const newDistance = Math.hypot(newDx, newDy)
  const scale = Math.max(0.01, newDistance / originalDistance)

  return /** @type {FloorPlanCorners} */ (
    corners.map(([lng, lat]) => [
      anchor[0] + (lng - anchor[0]) * scale,
      anchor[1] + (lat - anchor[1]) * scale,
    ])
  )
}

/**
 * Move one corner independently.
 *
 * @param {FloorPlanCorners} corners
 * @param {number} cornerIndex
 * @param {LngLat} newPosition
 * @returns {FloorPlanCorners}
 */
export function skewCorner(corners, cornerIndex, newPosition) {
  return /** @type {FloorPlanCorners} */ (
    corners.map((corner, index) => (index === cornerIndex ? [...newPosition] : [...corner]))
  )
}

/**
 * Rotate corners around centroid by radians.
 *
 * @param {FloorPlanCorners} corners
 * @param {number} angleRad
 * @returns {FloorPlanCorners}
 */
export function rotateCorners(corners, angleRad) {
  if (angleRad === 0) {
    return /** @type {FloorPlanCorners} */ (corners.map(([lng, lat]) => [lng, lat]))
  }

  const [cLng, cLat] = computeCentroid(corners)
  const cosA = Math.cos(angleRad)
  const sinA = Math.sin(angleRad)

  return /** @type {FloorPlanCorners} */ (
    corners.map(([lng, lat]) => {
      const x = lng - cLng
      const y = lat - cLat

      return [
        cLng + x * cosA - y * sinA,
        cLat + x * sinA + y * cosA,
      ]
    })
  )
}

/**
 * Compute the rotation handle above the top-edge midpoint.
 *
 * @param {FloorPlanCorners} corners
 * @param {number} [offsetFactor=0.3]
 * @returns {LngLat}
 */
export function computeRotationHandlePosition(corners, offsetFactor = 0.3) {
  const topLeft = corners[0]
  const topRight = corners[1]
  const bottomRight = corners[2]
  const bottomLeft = corners[3]

  const midpoint = [
    (topLeft[0] + topRight[0]) / 2,
    (topLeft[1] + topRight[1]) / 2,
  ]

  const leftHeight = Math.hypot(bottomLeft[0] - topLeft[0], bottomLeft[1] - topLeft[1])
  const rightHeight = Math.hypot(bottomRight[0] - topRight[0], bottomRight[1] - topRight[1])
  const imageHeight = (leftHeight + rightHeight) / 2

  if (imageHeight === 0) {
    return /** @type {LngLat} */ (midpoint)
  }

  const [cLng, cLat] = computeCentroid(corners)
  const dirX = midpoint[0] - cLng
  const dirY = midpoint[1] - cLat
  const dirLength = Math.hypot(dirX, dirY)

  if (dirLength === 0) {
    return /** @type {LngLat} */ (midpoint)
  }

  const offsetDistance = imageHeight * offsetFactor

  return [
    midpoint[0] + (dirX / dirLength) * offsetDistance,
    midpoint[1] + (dirY / dirLength) * offsetDistance,
  ]
}

/**
 * Compute angle from centroid to point.
 *
 * @param {LngLat} centroid
 * @param {LngLat} point
 * @returns {number}
 */
export function angleFromCentroid(centroid, point) {
  return Math.atan2(point[1] - centroid[1], point[0] - centroid[0])
}
